import { API, HOST } from '@/utils/http'
import { toast } from "@/components/ui/toast/use-toast"

const isMemberOf = (orgs, orgId) => orgs.some(o => o.organization_id === parseInt(orgId))

export const fetchOrgs = async ({commit, state}) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/organization`
    })

    commit('SET_ORGS', response.data)

    // Drop a remembered org the member no longer belongs to (different user on this
    // browser, removed from the org) so it can't be restored on the next load.
    if (state.selectedOrg && !isMemberOf(state.orgs, state.selectedOrg)) {
      commit('SET_SELECTED_ORG', null)
    }
  } catch (e) {
    toast({ title: "Can't fetch orgs", variant: 'destructive' })
  }
}

export const fetchOrg = async ({commit}, orgId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${orgId}`
    })

    commit('SET_CURRENT', response.data)
  } catch (e) {
    toast({ title: `Can't fetch org. ID: ${orgId}`, variant: 'destructive' })
  }
}

export const searchMembers = async (_context, { orgId, query, projectId }) => {
  const params = { q: query }
  if (projectId) params.project_id = projectId
  const response = await API({
    method: 'GET',
    url: `${HOST}/organization/${orgId}/members/search`,
    params
  })
  return response.data
}

export const fetchOrgMembers = async ({commit}, orgId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${orgId}/members`
    })

    commit('SET_CURRENT_MEMBERS', response.data)
  } catch (e) {
    toast({ title: `Can't fetch org members. ID: ${orgId}`, variant: 'destructive' })
  }
}

export const fetchOrgInvites = async ({commit, rootState}, orgId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${orgId}/invites`
    })

    commit('SET_CURRENT_INVITES', response.data)
  } catch (e) {
    // Silently ignore 403 Forbidden for non-super-admins (they don't have permission to view invites)
    // But show error for super-admins so they know if something is wrong
    const isSuperAdmin = rootState.user?.member?.is_super_user
    if (e.response?.status === 403 && !isSuperAdmin) {
      commit('SET_CURRENT_INVITES', [])
      return
    }
    toast({ title: `Can't fetch org invites. ID: ${orgId}`, variant: 'destructive' })
  }
}

export const invite = async ({commit}, payload) => {
  const orgId = payload.organization_id

  try {
    // Send first/last name along with fullname for backward compatibility
    const inviteData = {
      email: payload.email,
      fullname: payload.fullname,
      first_name: payload.first_name || null,
      last_name: payload.last_name || null,
      role_id: payload.role_id
    }

    const response = await API({
      method: 'POST',
      url: `${HOST}/organization/${orgId}/invite`,
      data: inviteData
    })

    commit('ADD_INVITE', response.data)
    toast({ title: `Invite sent to ${payload.email}` })
    return true
  } catch (e) {
    if (e.response?.status === 409) {
      toast({ title: 'User already in organization', variant: 'destructive' })
    } else if (e.response?.status === 400) {
      const detail = e.response.data?.detail
      const message = typeof detail === 'string' ? detail : 'Invalid invite data'
      toast({ title: message, variant: 'destructive' })
    } else if (e.response?.status === 422) {
      // Handle validation errors - show generic message
      toast({ title: 'Invalid invite data', variant: 'destructive' })
    } else {
      const detail = e.response?.data?.detail
      const message = typeof detail === 'string' ? detail : 'Failed to create invite'
      toast({ title: `Can't create invite. ${message}`, variant: 'destructive' })
    }
    return false
  }
}


export const fetchPendingOrgInvites = async ({commit}) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/invites/pending`
    })

    commit('SET_PENDING_INVITES', response.data)
  } catch (e) {
    toast({ title: "Can't fetch pending org invites", variant: 'destructive' })
  }
}

export const acceptOrgInvite = async ({commit}, inviteId) => {
  try {
    await API({
      method: 'PUT',
      url: `${HOST}/organization/invites/${inviteId}/accept`
    })

    commit('REMOVE_PENDING_INVITE', inviteId)
    toast({ title: 'Organization invite accepted', duration: 15000 })
  } catch (e) {
    toast({ title: "Can't accept invite", variant: 'destructive' })
  }
}

export const deleteOrgInvite = async ({commit}, payload) => {
  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/organization/${payload.orgId}/invites/${payload.inviteId}`
    })

    commit('REMOVE_INVITE', payload.inviteId)
    toast({ title: 'Invite deleted' })
  } catch (e) {
    toast({ title: "Can't delete invite", variant: 'destructive' })
  }
}

export const resendOrgInvite = async ({commit}, payload) => {
  try {
    await API({
      method: 'POST',
      url: `${HOST}/organization/${payload.orgId}/invites/${payload.inviteId}/resend`
    })

    toast({ title: 'Invite resent' })
  } catch (e) {
    toast({ title: "Can't resend invite", variant: 'destructive' })
  }
}

export const updateOrg = async (_context, {orgId, ...fields}) => {
  await API({
    method: 'PATCH',
    url: `${HOST}/organization/${orgId}`,
    data: fields
  })
}

export const updateOrgAvatar = async (_context, {orgId, file}) => {
  await API({
    headers: {'Content-Type': 'multipart/form-data'},
    method: 'PATCH',
    url: `${HOST}/organization/${orgId}/update-avatar`,
    data: {file}
  })
}

export const updateMember = async ({commit}, payload) => {
  try {
    const response = await API({
      method: 'PUT',
      url: `${HOST}/organization/${payload.orgId}/members/${payload.orgMemberId}`,
      data: {
        display_name: payload.displayName,
        key_settings: payload.keySettings
      }
    })
    commit('UPDATE_MEMBER', response.data)
    toast({ title: 'Member updated' })
  } catch (e) {
    toast({ title: `Can't update member. ${e.response.data?.detail}`, variant: 'destructive' })
  }
}

export const setMemberRole = async ({commit, dispatch, state}, {member, roleId}) => {
  try {
    await dispatch('access/assignOrgRole', {memberId: member.member_id, roleId}, {root: true})
    const current = state.currentMembers.find(m => m.organization_member_id === member.organization_member_id)
    commit('UPDATE_MEMBER', {...(current ?? member), role_id: roleId})
    toast({ title: 'Role changed' })
    return true
  } catch (e) {
    toast({ title: "Couldn't change the role. Reload and try again.", variant: 'destructive' })
    return false
  }
}

export const deleteMember = async ({commit}, payload) => {
  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/organization/${payload.orgId}/members/${payload.orgMemberId}`
    })
    commit('REMOVE_MEMBER', payload.orgMemberId)
    toast({ title: 'Member deleted' })
  } catch (e) {
    toast({ title: `Can't delete member. ${e.response.data?.detail}`, variant: 'destructive' })
  }
}

export const fetchApikeys = async ({commit}, orgId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${orgId}/apikeys`
    })

    commit('SET_APIKEYS', response.data)
  } catch (e) {
    toast({ title: `Can't get apikeys`, variant: 'destructive' })
  }
}

export const removeApiKey = async ({commit}, payload) => {
  try {
    const response = await API({
      method: 'DELETE',
      url: `${HOST}/organization/${payload.organization_id}/apikeys/${payload.uuid}`
    })

    commit('REMOVE_APIKEY', payload.uuid)
  } catch (e) {
    toast({ title: `Can't delete apikey`, variant: 'destructive' })
  }
}

export const addApiKey = async ({commit}, payload) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/organization/${payload.orgId}/apikeys`,
      data: {
        organization_id: payload.orgId,
        name: payload.name,
        key: payload.apikey
      }
    })

    commit('ADD_APIKEY', response.data)
  } catch (e) {
    console.log('error', e)
  }
}

export const setApiKeyAccess = async ({commit}, payload) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/organization/${payload.orgId}/members/${payload.orgMemberId}/apikey`
    })

    toast({ title: 'Access updated' })
  } catch (e) {
    console.log(e)
  }
}

export const updateApiKeySettings = async ({commit}, payload) => {
  try {
    await API({
      method: 'PUT',
      url: `${HOST}/organization/${payload.orgId}/members/${payload.orgMemberId}/apikey`,
      data: {
        settings: payload.keySettings
      }
    })
    toast({ title: 'Access updated' })
  } catch (e) {
    console.log(e)
  }
}

export const setSelectedOrg = async ({commit}, payload) => {
  try {
    commit('SET_SELECTED_ORG', payload)
  } catch (e) {
    toast({ title: 'Failed to set selected organization', variant: 'destructive' })
  }
}

let resolvePending = null

const resolve = async ({commit, dispatch, state}) => {
  if (!state.orgs.length) {
    await dispatch('fetchOrgs')
  }
  if (!state.orgs.length) return null

  if (state.selectedOrg && isMemberOf(state.orgs, state.selectedOrg)) {
    return state.selectedOrg
  }

  const fallbackOrgId = state.orgs[0].organization_id.toString()
  commit('SET_SELECTED_ORG', fallbackOrgId)
  return fallbackOrgId
}

// Returns the org id to use — the remembered one when it's still valid, otherwise
// the member's first org. Concurrent callers share one in-flight resolution so they
// can't each trigger fetchOrgs.
export const resolveSelectedOrg = async (context) => {
  if (resolvePending) return resolvePending

  resolvePending = resolve(context).catch(e => {
    toast({ title: 'Failed to initialize selected organization', variant: 'destructive' })
    return null
  })

  try {
    return await resolvePending
  } finally {
    resolvePending = null
  }
}
